import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createColumnHelper, useReactTable, getCoreRowModel, getSortedRowModel, getFilteredRowModel } from '@tanstack/react-table';
import { toast } from 'react-toastify';
import useTransactionStore from '../../store/transactions-store';
import useUserStore from '../../store/user-store';
import useOrganisationStore from '../../store/organisations-store';
import useSearchableFilter from '../../shared/hooks/useSearchableFilter';
import DataTable from '../../shared/components/table/DataTable';
import PageHeader from '../../shared/components/table/PageHeader';
import RowActions from '../../shared/components/table/RowActions';
import FilterSelect from '../../shared/components/table/FilterSelect';
import SearchableSelect from '../../shared/components/table/SearchableSelect';
import DeleteModal from '../../shared/components/modal/DeleteModal';
import { formatDate } from '../../shared/lib/formatters';
import TransactionsUpdateModal from './TransactionsUpdateModal';

const columnHelper = createColumnHelper();
const statusOptions = ['Pending', 'Approved'].map((status) => ({ value: status.toLowerCase(), label: status }));

const TransactionsPage = () => {
  const { transactions, fetchTransactions, updateTransaction, deleteTransaction } = useTransactionStore();
  const { searchUsers } = useUserStore();
  const { searchOrganisations } = useOrganisationStore();

  const [isUpdateOpen, setIsUpdateOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState(null);

  const {
    value: selectedOrg,
    label: selectedOrgLabel,
    onChange: setOrgSelection,
    loadOptions: loadOrgOptions,
  } = useSearchableFilter(searchOrganisations);

  const getUserSearchExtras = useCallback(() => ({ orgId: selectedOrg }), [selectedOrg]);
  const {
    value: selectedUser,
    label: selectedUserLabel,
    onChange: setUserSelection,
    clear: clearUserFilter,
    loadOptions: loadUserOptions,
  } = useSearchableFilter(searchUsers, getUserSearchExtras);

  const loadTransactions = useCallback(async () => {
    setIsLoading(true);
    await fetchTransactions(selectedUser || null, selectedOrg || null, filterStatus);
    setIsLoading(false);
  }, [fetchTransactions, selectedUser, selectedOrg, filterStatus]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  const handleOpenUpdateModal = (transaction) => {
    setSelectedTransaction(transaction);
    setIsUpdateOpen(true);
  };

  const handleOpenDeleteModal = (transaction) => {
    setSelectedTransaction(transaction);
    setIsDeleteOpen(true);
  };

  const handleCloseModal = () => {
    setIsUpdateOpen(false);
    setIsDeleteOpen(false);
    setSelectedTransaction(null);
  };

  const handleUpdateTransaction = async (id, transactionData) => {
    try {
      await updateTransaction(id, transactionData);
      toast.success('Transaction updated successfully');
      handleCloseModal();
    } catch (error) {
      console.error('Failed to update transaction:', error);
      toast.error('Failed to update transaction. Please try again.');
    }
  };

  const handleDeleteTransaction = async (id) => {
    try {
      await deleteTransaction(id);
      toast.success('Transaction deleted successfully');
      handleCloseModal();
    } catch (error) {
      console.error('Failed to delete transaction:', error);
      toast.error('Failed to delete transaction. Please try again.');
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('id', { header: 'ID' }),
      columnHelper.accessor('user_name', {
        header: 'User Name',
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('organisation', {
        header: 'Organisation',
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('status', { header: 'Status' }),
      columnHelper.accessor('amount', { header: 'Top-Up Amount' }),
      columnHelper.accessor('tokens', {
        header: 'Tokens',
        cell: (info) => info.getValue() || '0',
      }),
      columnHelper.accessor('type', { header: 'Type' }),
      columnHelper.accessor('created_at', {
        header: 'Created At',
        cell: (info) => formatDate(info.getValue()),
      }),
      columnHelper.accessor('actions', {
        header: 'Actions',
        cell: ({ row }) => (
          <RowActions
            onEdit={() => handleOpenUpdateModal(row.original)}
            onDelete={() => handleOpenDeleteModal(row.original)}
          />
        ),
      }),
    ],
    []
  );

  const table = useReactTable({
    data: transactions,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <div className="max-w-screen-3xl mx-auto mt-4">
      <PageHeader title="Transactions List" />
      <div className="mb-4 flex gap-4">
        <SearchableSelect
          name="org_id"
          value={selectedOrg}
          selectedLabel={selectedOrgLabel}
          onChange={(id, option) => {
            setOrgSelection(id, option);
            clearUserFilter();
          }}
          loadOptions={loadOrgOptions}
          placeholder="Select Organisation"
        />
        <SearchableSelect
          name="user_id"
          value={selectedUser}
          selectedLabel={selectedUserLabel}
          onChange={setUserSelection}
          loadOptions={loadUserOptions}
          placeholder="Select User"
        />
        <FilterSelect
          name="status"
          value={filterStatus}
          onChange={setFilterStatus}
          options={statusOptions}
          placeholder="Filter Status"
        />
      </div>
      <DataTable table={table} columnsCount={columns.length} isLoading={isLoading} emptyMessage="No transactions found" />
      <TransactionsUpdateModal
        isOpen={isUpdateOpen}
        onClose={handleCloseModal}
        transaction={selectedTransaction}
        onUpdate={handleUpdateTransaction}
      />
      <DeleteModal
        isOpen={isDeleteOpen}
        onClose={handleCloseModal}
        data={{ ...selectedTransaction, dataName: 'transaction' }}
        onDelete={handleDeleteTransaction}
      />
    </div>
  );
};

export default TransactionsPage;
